import React, { useContext } from "react"
import { observer } from "mobx-react"
import {
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Highlight,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title
} from "@mantine/core"
import {
  IconArrowLeft,
  IconExternalLink,
  IconMarkdown,
  IconSearch
} from "@tabler/icons-react"
import { AppWorkspaceContext } from "../../stores/AppWorkspace"
import { MarkdownDoc } from "../MarkdownDoc"
import { AGENT_DIRECTIONS, CATALOG, frameworkForStack, highlightTerms } from "../../../docs/catalog"
import { CopyAnchorButton } from "../CopyAnchorButton"
import { CopyAgentDirectionsButtons } from "../integrations/CopyAgentDirectionsButtons"
import { SiteVideo } from "../SiteVideo"
import { guidesVideo } from "../../helpers/siteVideo"
import { MobileHandoff } from "../MobileHandoff"
import { BTCPAY_SLUG, OWN_PATH_GUIDES, WORDPRESS_SLUG } from "../../helpers/constants"

// The search a phone visitor runs later from a computer, on the guides that
// have their own short path (/agents, /wordpress, /btcpay): ads land on them.
const HANDOFF_NAMES: Record<string, string> = { [WORDPRESS_SLUG]: "WordPress", [BTCPAY_SLUG]: "BTCPay" }

// Every guide opens with its own H1, and the page header above the body
// already draws the contract's title for it — so the body starts at the first
// paragraph. Only the rendering: the bundle, search and the markdown twin keep
// the heading, and the wizard strips it for its steps the same way.
// The page header already shows the title. The agent directions open with a
// one-line note to the agent before their H1; that note goes too.
const withoutLeadingH1 = (markdown: string): string =>
  markdown.replace(/^(?:[^#\n][^\n]*\n\n)?# [^\n]+\n+/, "")

const categoryLabel = (category: string) =>
  ({
    quickstart: "Quickstart",
    woocommerce: "WordPress + WooCommerce",
    btcpay: "BTCPay Server",
    agents: "Coding agents",
    "agent-directions": "Agent directions",
    guide: "Guides",
    security: "Security",
    operations: "Operations",
    reference: "Reference",
    recipe: "Recipes"
  })[category] ?? category

export const GuidesIndexPage: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext)
  const guides = workspace.guides
  const terms = highlightTerms(guides.query)
  const video = guidesVideo()

  return (
    <Stack gap="lg" py="md">
      <div>
        <Text className="or-eyebrow">Guides</Text>
        <Title>OpenReceive guides</Title>
        <Text c="dimmed" mt={6} maw={680}>
          Every OpenReceive guide: quickstarts for each framework and platform, agent directions,
          and how payments, swaps, storage and security work.
        </Text>
      </div>

      {/* The 20-minute technical deep dive: one request traced end to end. */}
      {video ? (
        <SiteVideo
          video={video}
          title="How OpenReceive works, end to end"
          label="How OpenReceive works: a 20-minute technical deep dive"
          sourceLocation="guides"
          className="or-guides-video"
          seekable
        />
      ) : null}

      <TextInput
        size="md"
        leftSection={<IconSearch size={18} />}
        placeholder="Search guides…"
        value={guides.query}
        onChange={(event) => workspace.setGuidesQuery(event.currentTarget.value)}
      />

      {guides.groupedGuides.map((group) => (
        <Stack key={group.category} gap="sm">
          <Title order={3}>{categoryLabel(group.category)}</Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            {group.guides.map((guide) => (
              <Card
                key={guide.slug}
                withBorder
                padding="lg"
                radius="md"
                component="a"
                href={guide.path}
                className="or-guide-card"
                onClick={(event) => {
                  event.preventDefault()
                  workspace.goGuides(guide.slug)
                }}
              >
                <Group justify="space-between" mb={6}>
                  <Text fw={700}>
                    {terms.length ? <Highlight highlight={terms}>{guide.title}</Highlight> : guide.title}
                  </Text>
                  <Badge variant="light" size="sm">
                    {categoryLabel(guide.category)}
                  </Badge>
                </Group>
                <Text size="sm" c="dimmed" lineClamp={3}>
                  {terms.length ? <Highlight highlight={terms}>{guide.excerpt}</Highlight> : guide.excerpt}
                </Text>
              </Card>
            ))}
          </SimpleGrid>
        </Stack>
      ))}

      {guides.visibleGuides.length === 0 ? (
        <Paper withBorder p="xl">
          <Text>No guides matched that search.</Text>
        </Paper>
      ) : null}
    </Stack>
  )
})

export const GuidePage: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext)
  const guide = workspace.guides.current

  // Non-null only on /guides/agent-directions-node and -rails.
  const payload = guide
    ? AGENT_DIRECTIONS.find((entry) => entry.guidePath === guide.path) ?? null
    : null

  // Every guide's markdown is its own chunk, fetched on the first guide page.
  if (workspace.guides.loading) {
    return (
      <Center py="xl">
        <Loader color="orGreen" />
      </Center>
    )
  }

  if (workspace.guides.missing) {
    return (
      <Stack py="xl" gap="md">
        <Title>Guide not found</Title>
        <Text c="dimmed">That URL is not one of the public OpenReceive guides.</Text>
        <Button variant="light" onClick={() => workspace.goGuides()}>
          All guides
        </Button>
      </Stack>
    )
  }

  if (!guide) return <GuidesIndexPage />

  return (
    <div className="or-guide-layout">
      <aside className="or-guide-toc">
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconArrowLeft size={14} />}
          onClick={() => workspace.goGuides()}
        >
          All guides
        </Button>
        <Text size="xs" tt="uppercase" fw={700} c="dimmed" mt="md" mb={6}>
          On this page
        </Text>
        <Stack gap={4}>
          {guide.headings
            .filter((heading) => heading.level >= 2)
            .map((heading) => (
              <Anchor key={heading.id} href={`#${heading.id}`} size="sm" c="dimmed">
                {heading.title}
              </Anchor>
            ))}
        </Stack>
      </aside>

      <article>
        <Group justify="space-between" align="flex-start" mb="md">
          <div>
            <Text className="or-eyebrow">
              {categoryLabel(guide.kind === "agent-directions" ? "agent-directions" : guide.category)}
            </Text>
            <Title>{guide.title}</Title>
          </div>
          <Group gap="xs">
            <CopyAnchorButton anchor="" path={guide.path} label="Copy page URL" />
            {/* This page is rendered in the browser, so it is unreadable to
                anything fetching the URL. The twin is the same document as raw
                markdown, and is what the agent directions link. */}
            <Button
              component="a"
              href={guide.markdownPath}
              variant="default"
              size="xs"
              leftSection={<IconMarkdown size={14} />}
              title="This page as raw markdown, for a coding agent"
            >
              Markdown
            </Button>
            <Button
              component="a"
              href={guide.githubUrl}
              variant="default"
              size="xs"
              target="_blank"
              rel="noopener noreferrer"
              leftSection={<IconExternalLink size={14} />}
            >
              Source
            </Button>
          </Group>
        </Group>
        {OWN_PATH_GUIDES[guide.slug] ? (
          <MobileHandoff
            name={HANDOFF_NAMES[guide.slug]}
            shortPath={OWN_PATH_GUIDES[guide.slug]}
            pageKind="guide"
            pageId={guide.slug}
          />
        ) : null}
        {payload ? (
          // This page IS the payload, rendered for a person. Anyone who got here
          // meaning to hand it to an agent should not have to select 16 KB of
          // markdown by hand.
          <Paper withBorder radius="md" p="md" mb="lg">
            <Group justify="space-between" align="center" wrap="wrap" gap="sm">
              <Text size="sm" c="dimmed" maw={520} style={{ overflowWrap: "anywhere" }}>
                Directions for a coding agent, complete on their own — paste them into Cursor,
                Claude or Codex, or point the agent at{" "}
                <Anchor href={payload.path} target="_blank" rel="noopener noreferrer">
                  {CATALOG.siteOrigin}
                  {payload.path}
                </Anchor>{" "}
                ({(payload.bytes / 1024).toFixed(1)} KB of markdown).
              </Text>
              <CopyAgentDirectionsButtons
                stack={payload.stack}
                framework={frameworkForStack(payload.stack)?.id ?? payload.stack}
                ctaLocation="agent_directions_guide"
                size="sm"
                preload
              />
            </Group>
          </Paper>
        ) : null}

        <MarkdownDoc markdown={withoutLeadingH1(guide.markdown)} />
        <Text size="xs" c="dimmed" mt="xl">
          Synced from {guide.sourcePath} · OpenReceive {CATALOG.sourceVersion}
        </Text>
      </article>
    </div>
  )
})
